import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { PanelMigrationStatus } from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';
import { hasValue } from '~/lib/helper/ArrayHelper';
import { withPathname } from '~/lib/helper/LocationHelper';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import { AsyncStore } from '~/lib/store/AsyncStore';

import './ViewInMpanel.scss';

type PanelSection =
    | 'server'
    | 'loadbalancer'
    | 'network'
    | 'domain'
    | 'sshkey'
    | 'services'
    | 'domains'
    | 'add-server'
    | 'add-network'
    | 'add-loadbalancer'
    | 'add-domain-name'
    | 'add-domain-hosting'
    | 'billing'
    | 'account'
    | 'add-domain-registration'
    | 'login'
    | 'register'
    | 'signup';

type ViewInMpanelProps = {
    section: PanelSection;
    pathname: string;
    domainName?: string;
    serverHostName?: string;
    loadBalancerUniqueName?: string;
    vpcUniqueName?: string;
    unauthenticated?: boolean;
};

const pathMappings: Record<PanelSection, Record<string, string>> = {
    services: {
        '/servers': '',
        '/networks': '',
        '/loadbalancers': '',
    },
    server: {
        '/overview': '/manage/[serverHostName]',
        '/settings/alerts': '/alerts/[serverHostName]',
        '/change-plan': '/change-plan/[serverHostName]',
        '/recovery/change-password': '/reset-password/[serverHostName]',
        '/settings/partner': '/set-partner/[serverHostName]',
        '/network/port-block': '/port-blocking/[serverHostName]',
        '/network/firewall': '/firewall/[serverHostName]',
        '/settings/disks': '/disks/[serverHostName]',
        '/backups/manage': '/backups/[serverHostName]',
        '/backups/create': '/backups/[serverHostName]',
        '/backups/upload': '/backups/[serverHostName]/upload',
        '/backups/settings': '/backups/[serverHostName]/settings',
        '/recovery/reinstall': '/reinstall/[serverHostName]',
        '/recovery/rescue': '/manage/[serverHostName]',
        '/usage': '/statistics/[serverHostName]',
        '/settings/advanced': '/advanced-settings/[serverHostName]',
        '/settings/hostname': '/change-hostname/[serverHostName]',
        '/network/vpc-advanced': '/router/[serverHostName]',
        '/network/ips/failoverips': '/ipfailover/[serverHostName]',
        '/cancel-server': '/cancel-vps/[serverHostName]',
        '/network/ips': '/change-ipaddress/[serverHostName]',
    },
    loadbalancer: {
        '/settings': '/manage-loadbalancer/[loadBalancerUniqueName]',
        '/servers': '/manage-loadbalancer/[loadBalancerUniqueName]',
        '/cancel': '/cancel-loadbalancer/[loadBalancerUniqueName]',
    },
    network: {
        '/settings/routing': '/routes-network/[vpcUniqueName]',
        '/delete': '/cancel-network/[vpcUniqueName]',
    },
    domain: {
        '/dns': '/dns/[domainName]/manage',

        '/registration/nameservers': '/manage-domain-name/[domainName]',
        '/registration/registrant': '/manage-domain-name/[domainName]',
        '/registration/lock': '/manage-domain-name/[domainName]',
        '/registration/authcode': '/manage-domain-name/[domainName]',
        '/registration/renew': '/manage-domain-name/[domainName]',

        '/renew': '/renew-domain-name/bnrla.com',
    },
    sshkey: {
        '/': '/ssh-keys',
    },
    domains: {
        '/': '/domains',
    },
    'add-server': {
        '/': '/add-vps',
    },
    'add-network': {
        '/': '/add-network',
    },
    'add-loadbalancer': {
        '/': '/add-loadbalancer',
    },
    'add-domain-hosting': {
        '/': '/dns/add',
    },
    'add-domain-registration': {
        '/': '/add-domain-name',
    },
    billing: {
        '/pending': '/profile/balance',
        '/invoices': '/invoices',
        '/payment-details': '/profile/billing',
    },
    'add-domain-name': {
        '/': '/add-domain-name',
    },
    account: {
        '/': '/profile/overview',
    },
    login: {
        '/': '/../login',
        '/forgotpassword': '/../customer/recoverpassword',
    },
    register: {
        '/': '/../register',
    },
    signup: {
        '/': '/../vps/signup',
        '/server-details': '/../vps/signup',
        '/contact-information': '/../vps/signup',
        '/confirm-email': '/../vps/signup',
    },
};

export function pathToMpanelPath(
    section: PanelSection,
    pathname: string,
    substitutions: Record<string, string | undefined>
): string | undefined {
    let hasFailedSubstitution = false;
    const path = pathMappings[section][pathname]?.replace(/\[([^\]]+)\]/g, (match, key: string) => {
        const substitutionValue = substitutions[key];
        if (hasValue(substitutionValue)) {
            return substitutionValue;
        } else {
            hasFailedSubstitution = true;
            return '';
        }
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- eslint has trouble knowing that the lambda above may set the boolean.
    return hasFailedSubstitution ? undefined : path;
}

@observer
export class ViewInMpanel extends React.Component<ViewInMpanelProps> {
    migrationStatusStore: AsyncStore<PanelMigrationStatus, undefined>;

    mountHelper = new UnmountHelper(this);

    @observable
    redirectPromise?: Promise<void> = undefined;

    constructor(props: ViewInMpanelProps) {
        super(props);
        this.migrationStatusStore = new AsyncStore(
            () => getAppContext().siteApiClient.getPanelMigrationStatus(),
            undefined
        );
        makeObservable(this);
    }

    componentDidMount() {
        if (this.props.unauthenticated) {
            return;
        }

        void this.migrationStatusStore.load();
    }

    async setMonorailPreferred() {
        try {
            await getAppContext().siteApiClient.updatePanelMigrationStatus({ migration_status: 'monorail-preferred' });
        } catch (e) {
            console.warn("Couldn't update user preference", e);
        }
    }

    async switchToClassicPanel(path: string) {
        // If they are switching and currently prefer react, we update their preference to monorail (unless they are admin)
        if (
            !this.props.unauthenticated &&
            !getAppContext().customer.isAdminLogin &&
            this.migrationStatusStore.item?.migration_status === 'react-preferred'
        ) {
            await this.mountHelper.cancelIfUnmounted(this.setMonorailPreferred());
        }

        getAppContext().navigate(path);
    }

    updateAndRedirect(path: string) {
        this.redirectPromise = this.switchToClassicPanel(path);
        return false;
    }

    render() {
        const migrationStatus = this.migrationStatusStore.item;

        if (!migrationStatus && !this.props.unauthenticated) return null; // Don't show button until we know our status

        const showButton =
            getAppContext().customer.isAdminLogin ||
            !['react', 'react-only'].includes(migrationStatus?.migration_status ?? '');

        if (!showButton) return null;

        const { section, pathname, ...substitutions } = this.props;
        const mpanelRoot = getAppContext().config.wwwSite + '/mpanel';
        const mpanelPath = pathToMpanelPath(section, pathname, substitutions as Record<string, string | undefined>);

        if (!hasValue(mpanelPath)) return null;

        const destination = mpanelRoot + mpanelPath;
        return (
            <div className="viewInMpanel">
                {this.redirectPromise ? (
                    <span className="redirecting">Redirecting...</span>
                ) : (
                    <a href={destination} onClick={() => this.updateAndRedirect(destination)}>
                        <i className="icon ion-md-swap me-1"></i> Return to classic mPanel
                    </a>
                )}
            </div>
        );
    }
}

export default withPathname(ViewInMpanel);
